import { Link } from "react-router-dom";
import { Mail, MapPin, ExternalLink, Settings2 } from "lucide-react";
import { openPreferences } from "@/lib/cookieConsent";

const PRODUCTO = [
  { label: "Escáner gratuito", to: "/" },
  { label: "Módulo Pro", to: "/modulo-pro" },
  { label: "Deep Scan multi-página", to: "/cuenta" },
  { label: "Simulador de multas", to: "/simulador-multas" },
  { label: "Transparencia", to: "/transparencia" },
];

const RECURSOS = [
  { label: "Blog", to: "/blog" },
  { label: "Casos · Antes/Después", to: "/casos" },
  { label: "Guías por plataforma", to: "/guias" },
  { label: "Checklist Art. 14 ter", to: "/cumplimiento-art-14-ter" },
];

const LEGAL = [
  { label: "Política de Privacidad", to: "/politica-privacidad" },
  { label: "Política de Cookies", to: "/politica-cookies" },
  { label: "Ejercer Derechos ARCO", to: "/ejercer-derechos" },
];

const EMPRESA = [
  { label: "Sobre nosotros", to: "/sobre-nosotros" },
  { label: "Contacto", to: "/contacto" },
];

const LEGAL_EXT = [
  { label: "Ley 21.719 (BCN)", href: "https://www.bcn.cl/leychile/navegar?idNorma=141599" },
  { label: "Guía Práctica Min. Segpres", href: "https://wikiguias.digital.gob.cl/datos-personales/guia-practica-implementacion-nueva-ley-datos-personales" },
  { label: "Cláusulas Min. Economía (RAEX 2025-03731)", href: "https://www.economia.gob.cl/2025/12/10/raex202503731-aprueba-las-clausulas-contractuales-modelo-para-transferencias-internacionales-que-indica.htm" },
];

export default function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="relative mt-24 bg-ink-950 text-slate-300 overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-2.5">
              <svg width="32" height="32" viewBox="0 0 64 64" aria-hidden="true">
                <defs>
                  <linearGradient id="ldcFooterGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#1e40af" />
                  </linearGradient>
                </defs>
                <path d="M32 4 L56 12 V32 C56 48 32 60 32 60 C32 60 8 48 8 32 V12 Z" fill="url(#ldcFooterGrad)" />
                <rect x="20" y="24" width="24" height="3.5" rx="1.2" fill="#FFFFFF" />
                <rect x="20" y="31" width="18" height="3.5" rx="1.2" fill="#FFFFFF" opacity="0.85" />
                <rect x="20" y="38" width="14" height="3.5" rx="1.2" fill="#FFFFFF" opacity="0.7" />
              </svg>
              <span className="leading-none">
                <span className="font-display text-xl text-white block">Ley de Datos</span>
                <span className="font-mono tracking-[0.22em] text-[9px] text-blue-300 uppercase block mt-0.5">Chile · 21.719</span>
              </span>
            </div>
            <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-sm">
              Sistema chileno de cumplimiento de la Ley de Protección de Datos Personales. Escáner gratuito + módulo Pro con documentos legales generados con IA, alineados a la Guía oficial del Min. Segpres.
            </p>
            <div className="mt-6 flex items-start gap-2.5 text-xs text-slate-400">
              <Mail className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <a href="mailto:privacidad@leydedatoschile.com" className="hover:text-white transition-colors">
                privacidad@leydedatoschile.com
              </a>
            </div>
            <div className="mt-2 flex items-start gap-2.5 text-xs text-slate-400">
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>Santiago, Chile</span>
            </div>
          </div>

          {/* Producto */}
          <div className="md:col-span-2">
            <p className="font-mono text-[10px] tracking-[0.18em] text-blue-300 uppercase mb-4">Producto</p>
            <ul className="space-y-2.5">
              {PRODUCTO.map((l) => (
                <li key={l.to + l.label}>
                  <Link to={l.to} className="text-sm text-slate-300 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Recursos */}
          <div className="md:col-span-2">
            <p className="font-mono text-[10px] tracking-[0.18em] text-blue-300 uppercase mb-4">Recursos</p>
            <ul className="space-y-2.5">
              {RECURSOS.map((l) => (
                <li key={l.to + l.label}>
                  <Link to={l.to} className="text-sm text-slate-300 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="md:col-span-2">
            <p className="font-mono text-[10px] tracking-[0.18em] text-blue-300 uppercase mb-4">Legal</p>
            <ul className="space-y-2.5">
              {LEGAL.map((l) => (
                <li key={l.to + l.label}>
                  <Link to={l.to} className="text-sm text-slate-300 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  onClick={openPreferences}
                  data-testid="footer-cookie-preferences"
                  className="text-sm text-slate-300 hover:text-white transition-colors inline-flex items-center gap-1.5"
                >
                  <Settings2 className="w-3 h-3 opacity-70" />
                  Preferencias de cookies
                </button>
              </li>
            </ul>
          </div>

          {/* Empresa */}
          <div className="md:col-span-2">
            <p className="font-mono text-[10px] tracking-[0.18em] text-blue-300 uppercase mb-4">Empresa</p>
            <ul className="space-y-2.5">
              {EMPRESA.map((l) => (
                <li key={l.to + l.label}>
                  <Link to={l.to} className="text-sm text-slate-300 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Fuentes oficiales (banda horizontal) */}
        <div className="mt-12 pt-6 border-t border-white/10">
          <p className="font-mono text-[10px] tracking-[0.18em] text-blue-300 uppercase mb-3">Fuentes oficiales</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {LEGAL_EXT.map((l) => (
              <a
                key={l.href}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1.5"
              >
                {l.label}
                <ExternalLink className="w-3 h-3 opacity-60" />
              </a>
            ))}
          </div>
        </div>

        {/* ARCO + legal notice */}
        <div className="mt-10 pt-8 border-t border-white/10 grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-7">
            <p className="text-xs text-slate-400 leading-relaxed">
              <span className="font-semibold text-white">Ejerce tus derechos</span> (acceso, rectificación, cancelación, oposición + portabilidad) en{" "}
              <Link to="/ejercer-derechos" className="text-blue-300 underline hover:text-white">
                /ejercer-derechos
              </Link>
              {" "}o escribiendo a{" "}
              <a href="mailto:privacidad@leydedatoschile.com" className="text-blue-300 underline hover:text-white">
                privacidad@leydedatoschile.com
              </a>
              . Respondemos en máximo 30 días corridos (Ley 21.719). Responsable: Ley de Datos Chile. Lee la{" "}
              <Link to="/politica-privacidad" className="text-blue-300 underline hover:text-white">política de privacidad</Link> y la{" "}
              <Link to="/politica-cookies" className="text-blue-300 underline hover:text-white">política de cookies</Link>.
            </p>
          </div>
          <div className="md:col-span-5 md:text-right text-xs text-slate-500">
            <p>
              © {year} Ley de Datos Chile. Hecho en Chile <span aria-hidden="true">🇨🇱</span>
            </p>
            <p className="mt-1 font-mono text-[10px] text-slate-600">
              v4.0 · build {new Date().toISOString().slice(0, 10).replace(/-/g, ".")}
            </p>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="mt-8 text-[11px] text-slate-500 leading-relaxed max-w-4xl">
          <span className="font-semibold text-slate-300">Descargo legal:</span> El informe es orientativo y no constituye asesoría legal. El análisis se basa en señales técnicas detectables públicamente en tu sitio web y no reemplaza la revisión de un abogado especialista en protección de datos. Para cumplimiento formal de la Ley 21.719 consulta con un profesional.
        </p>
      </div>
    </footer>
  );
}
